import type {
  PinBasketData,
  PinBasketItem,
  PinIdentifier,
  PinPaymentRegion,
  PinPaymentSuccessData
} from '../types';

import {InfoCircleOutlined} from '@ant-design/icons';
import {Button, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {
  confirmPayment,
  getPinBasket,
  paymentCheck,
  removeFromPinBasket
} from 'store/slices/pin/thunk';
import {numberFormat} from 'utils/Helper';

import PinInfoRow from '../components/common/PinInfoRow/PinInfoRow';
import PinPackageGroups from '../components/common/PinPackageGroups/PinPackageGroups';
import {PIN_ROUTES} from '../constants';
import {getPinErrorMessage, getPinVendorId} from '../utils';
import OtpPaymentSheet from './components/OtpPaymentSheet/OtpPaymentSheet';

const OTP_COUNTDOWN_SECONDS = 120;

const PinBasket: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const vendorId = getPinVendorId();
  const {pinBasket, payment, confirmPaymentLoading, loading} = useAppSelector((state) => state.pin);
  const basket = (pinBasket ?? {}) as PinBasketData;
  const [otp, setOtp] = useState('');
  const [countDown, setCountDown] = useState(0);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [removingPinId, setRemovingPinId] = useState<PinIdentifier>();
  const regions: PinPaymentRegion[] = (basket.regions ?? []).map((region) => ({
    name: region.region_name,
    items: region.items ?? []
  }));

  useEffect(() => {
    void dispatch(getPinBasket({vendor_id: vendorId}));
  }, [dispatch, vendorId]);

  const removePin = async (pinId: PinIdentifier) => {
    setRemovingPinId(pinId);
    try {
      await dispatch(removeFromPinBasket({vendor_id: vendorId, basket_item: pinId})).unwrap();
      await dispatch(getPinBasket({vendor_id: vendorId})).unwrap();
    } catch (error) {
      toast(getPinErrorMessage(error, 'خطا در حذف بسته'));
    } finally {
      setRemovingPinId(undefined);
    }
  };

  const requestOtp = async () => {
    if (countDown > 0) {
      setIsPaymentOpen(true);
      return;
    }

    setOtp('');
    try {
      await dispatch(paymentCheck({vendor_id: vendorId})).unwrap();
      setCountDown(OTP_COUNTDOWN_SECONDS);
      setIsPaymentOpen(true);
    } catch (error) {
      toast(getPinErrorMessage(error, 'خطا در ارسال رمز یکبار مصرف'));
    }
  };

  const resendOtp = async () => {
    setOtp('');
    try {
      await dispatch(paymentCheck({vendor_id: vendorId})).unwrap();
      setCountDown(OTP_COUNTDOWN_SECONDS);
    } catch (error) {
      toast(getPinErrorMessage(error, 'خطا در ارسال دوباره رمز'));
    }
  };

  const finalizePayment = async () => {
    if (otp.length !== 5) {
      toast('لطفا کد پیامک شده را وارد کنید');
      return;
    }

    try {
      const response = await dispatch(confirmPayment({vendor_id: vendorId, code: otp})).unwrap();
      const paymentData = (response?.data ?? {}) as Omit<PinPaymentSuccessData, 'items'> & {
        items?: Array<PinBasketItem & {region_name?: string}>;
      };
      const groupedItems = (paymentData.items ?? []).reduce<Record<string, PinBasketItem[]>>(
        (groups, item) => {
          const regionName = item.region_name ?? '';
          groups[regionName] = [...(groups[regionName] ?? []), item];
          return groups;
        },
        {}
      );
      const items = Object.entries(groupedItems).map(([name, regionItems]) => ({
        name,
        items: regionItems
      }));

      navigate(PIN_ROUTES.paymentSuccess, {
        state: {...paymentData, items}
      });
    } catch {
      void dispatch(getPinBasket({vendor_id: vendorId}));
      toast('کد اشتباه است');
    }
  };

  if (loading && regions.length === 0) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" color="primary" />
      </div>
    );
  }

  return (
    <PageLayout
      footerClassName="h-auto! flex-col p-0!"
      footer={
        <div className="flex flex-col">
          <div className="border-b border-gray-03">
            <PinInfoRow title="مبلغ فاکتور" value={`${numberFormat(basket.price ?? 0)} تومان`} />
            <PinInfoRow title="درصد مالیات" value={`${basket.tax_rate ?? 0}%`} />
            <PinInfoRow
              title="مبلغ قابل پرداخت"
              value={`${numberFormat(basket.total_price ?? 0)} تومان`}
              hasBorder={false}
            />
          </div>
          <div className="p-2.5">
            <Button
              fullWidth
              disabled={removingPinId != null || regions.length === 0}
              onClick={() => void requestOtp()}
            >
              پرداخت اعتباری
            </Button>
          </div>
        </div>
      }
    >
      <div className="m-2.5 flex gap-2.5 rounded-xl bg-blue-mist p-2.5 text-body-sm text-neutral-fg">
        <InfoCircleOutlined aria-hidden className="mt-1 shrink-0 text-body-lg" />
        <p>بسته‌ها به مدت ۱۰ دقیقه برای شما رزرو شده است. پیش از پایان، اقدام به خرید کنید.</p>
      </div>
      <PinPackageGroups
        regions={regions}
        mode="basket"
        removingPinId={removingPinId}
        onRemove={(pinId) => void removePin(pinId)}
      />
      <OtpPaymentSheet
        show={isPaymentOpen}
        onClose={() => setIsPaymentOpen(false)}
        otp={otp}
        setOtp={setOtp}
        countDown={countDown}
        setCountDown={setCountDown}
        mobileNumber={payment?.number}
        totalPrice={basket.total_price ?? 0}
        confirmPaymentLoading={Boolean(confirmPaymentLoading)}
        onResend={() => void resendOtp()}
        onFinalize={() => void finalizePayment()}
      />
    </PageLayout>
  );
};

export default PinBasket;
